import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  OPERATIONAL_REENTRY_POLICIES,
  REENTRY_POLICIES,
  Status,
  presentation,
} from '@gatecheckpro/design-system';
import type { OrganizerEvent, Venue } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

/**
 * Gates and the zones they admit to, for this event.
 *
 * The distinction the page has to teach without a manual: a *gate* is a door
 * that exists at the venue; a *gate policy* is how that door behaves on this
 * night. The same door admits different people at a family matinee and a
 * backstage-heavy festival, so the rules belong to the event.
 *
 * None of this is ever public (SDAD Section 40): telling attendees which
 * entrance allows re-entry tells everyone else too.
 */
export function EventGatesPage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const command = useCommand<OrganizerEvent>();
  const base = `/api/v1/organizer/organizations/${organizationId}`;

  const { resource: venue } = useResource<Venue>(
    event.venue === null ? null : `${base}/venues/${event.venue.venueId}`,
  );

  const [editingGateId, setEditingGateId] = useState<string | null>(null);
  const [zoneIds, setZoneIds] = useState<string[]>([]);
  const [reentry, setReentry] = useState('NONE');
  const [operationalReentry, setOperationalReentry] = useState('NONE');
  const [identityCheck, setIdentityCheck] = useState(false);

  if (event.venue === null) {
    return (
      <EmptyState
        title="Choose a venue first"
        action={
          <Link to="../venue" className="gc-button gc-button--primary">
            Go to Venue
          </Link>
        }
      >
        Gates belong to a venue. Once the event has one, you can decide how each of its entrances
        behaves on this night.
      </EmptyState>
    );
  }

  const configuredGateIds = new Set(event.gatePolicies.map((policy) => policy.gateId));
  const availableGates =
    venue.status === 'loaded' ? venue.data.gates.filter((gate) => gate.status === 'ACTIVE') : [];
  const unconfigured = availableGates.filter((gate) => !configuredGateIds.has(gate.gateId));

  function startEditing(gateId: string): void {
    const existing = event.gatePolicies.find((policy) => policy.gateId === gateId);
    setEditingGateId(gateId);
    setZoneIds(existing?.zones.map((zone) => zone.accessZoneId) ?? []);
    setReentry(existing?.reentryPolicy ?? 'NONE');
    setOperationalReentry(existing?.operationalReentryPolicy ?? 'NONE');
    setIdentityCheck(existing?.identityCheckRequired ?? false);
  }

  async function save(): Promise<void> {
    if (editingGateId === null || zoneIds.length === 0) return;
    const result = await command.run(`${base}/events/${event.eventId}/gate-policies`, {
      method: 'PUT',
      body: {
        gateId: editingGateId,
        reentryPolicy: reentry,
        operationalReentryPolicy: operationalReentry,
        identityCheckRequired: identityCheck,
        accessZoneIds: zoneIds,
      },
    });
    if (result !== null) {
      setEditingGateId(null);
      reload();
    }
  }

  return (
    <div className="gc-stack gc-stack--loose">
      <Notice tone="info" title="None of this is public">
        Gate rules, admission windows and re-entry policy stay inside GateCheckPro. Telling
        attendees which entrance allows re-entry would tell everyone else too.
      </Notice>

      <FailureNotice error={command.error} />

      {event.gatePolicies.length === 0 ? (
        <EmptyState title="No gates configured for this event">
          An event needs at least one gate before it can be published — without one there is nothing
          to build a Gate Package from, and no door for a scanner to be assigned to.
        </EmptyState>
      ) : (
        <div className="gc-stack">
          {event.gatePolicies.map((policy) => {
            const reentryState = presentation(REENTRY_POLICIES, policy.reentryPolicy);
            return (
              <Card
                key={policy.eventGatePolicyId}
                title={policy.gateDisplayName}
                eyebrow={`Gate ${policy.gateCode} · revision ${String(policy.revision)}`}
                action={<Button onClick={() => startEditing(policy.gateId)}>Reconfigure</Button>}
              >
                <div className="gc-stack gc-stack--tight">
                  <div className="gc-row">
                    <Status
                      tone={reentryState.tone}
                      glyph={reentryState.glyph}
                      label={reentryState.label}
                      title={reentryState.meaning}
                    />
                    {policy.isAccessibleEntry && (
                      <Status tone="info" glyph="♿" label="Accessible entry" />
                    )}
                    {policy.identityCheckRequired && (
                      <Status tone="caution" glyph="⚑" label="Identity check" />
                    )}
                  </div>
                  <p className="gc-muted">
                    Admits to {policy.zones.map((zone) => zone.displayName).join(', ')}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editingGateId !== null && venue.status === 'loaded' && (
        <Card title="Gate rules for this event" eyebrow="Applies to this event only">
          <div className="gc-stack">
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="gc-field__label" style={{ marginBottom: '0.5rem' }}>
                Which zones does this gate admit to?
              </legend>
              <div className="gc-grid">
                {venue.data.zones.map((zone) => (
                  <label key={zone.accessZoneId} className="gc-checkbox">
                    <input
                      type="checkbox"
                      checked={zoneIds.includes(zone.accessZoneId)}
                      onChange={(input) =>
                        setZoneIds((current) =>
                          input.target.checked
                            ? [...current, zone.accessZoneId]
                            : current.filter((id) => id !== zone.accessZoneId),
                        )
                      }
                    />
                    <span className="gc-checkbox__body">
                      <span className="gc-checkbox__title">{zone.displayName}</span>
                      <span className="gc-muted gc-mono">{zone.code}</span>
                    </span>
                  </label>
                ))}
              </div>
              {venue.data.zones.length === 0 && (
                <p className="gc-muted">
                  This venue has no zones yet.{' '}
                  <Link to={`/o/${organizationId}/venues/${event.venue.venueId}`}>Add one</Link>.
                </p>
              )}
            </fieldset>

            <div className="gc-field">
              <label className="gc-field__label" htmlFor="reentry">
                Ticket re-entry
              </label>
              <select
                id="reentry"
                className="gc-select"
                value={reentry}
                onChange={(input) => setReentry(input.target.value)}
              >
                {Object.entries(REENTRY_POLICIES).map(([value, state]) => (
                  <option key={value} value={value} disabled={value !== 'NONE'}>
                    {state.label} — {state.meaning}
                    {value === 'NONE' ? '' : ' (not available yet)'}
                  </option>
                ))}
              </select>
              {/* Said here rather than discovered at publication. An organizer
                  who could pick a ticket re-entry policy and only learn it was
                  unsupported when the Gate Package refused to build has been
                  told the wrong thing by the form. */}
              <p className="gc-muted">
                GateCheckPro admits each ticket once per session. Ticket re-entry is not enforceable
                yet, so an event whose gates allow it cannot be prepared for scanners.
              </p>
            </div>

            <div className="gc-field">
              <label className="gc-field__label" htmlFor="operational-reentry">
                Crew, vendor and staff re-entry
              </label>
              <select
                id="operational-reentry"
                className="gc-select"
                value={operationalReentry}
                onChange={(input) => setOperationalReentry(input.target.value)}
              >
                {Object.entries(OPERATIONAL_REENTRY_POLICIES).map(([value, state]) => (
                  <option key={value} value={value}>
                    {state.label} — {state.meaning}
                  </option>
                ))}
              </select>
              <p className="gc-muted">
                Operational badges are decided by the venue, separately from tickets. Someone
                working a load-in crosses a door repeatedly; a guest doing the same is a different
                question.
              </p>
            </div>

            <label className="gc-checkbox">
              <input
                type="checkbox"
                checked={identityCheck}
                onChange={(input) => setIdentityCheck(input.target.checked)}
              />
              <span className="gc-checkbox__body">
                <span className="gc-checkbox__title">Require an identity check</span>
                <span className="gc-muted">
                  Staff must check ID before admitting. Use only where it is lawful and needed.
                </span>
              </span>
            </label>

            <div className="gc-row">
              <Button
                variant="primary"
                busy={command.busy}
                disabled={zoneIds.length === 0}
                onClick={() => void save()}
              >
                Save gate rules
              </Button>
              <Button onClick={() => setEditingGateId(null)}>Cancel</Button>
              {zoneIds.length === 0 && <span className="gc-muted">Choose at least one zone.</span>}
            </div>
          </div>
        </Card>
      )}

      {unconfigured.length > 0 && (
        <Card title="Gates not yet configured" eyebrow={event.venue.name}>
          <div className="gc-stack">
            {unconfigured.map((gate) => (
              <div key={gate.gateId} className="gc-row gc-row--between">
                <div className="gc-stack gc-stack--tight">
                  <strong>{gate.displayName}</strong>
                  <span className="gc-muted gc-mono">{gate.code}</span>
                </div>
                <Button onClick={() => startEditing(gate.gateId)}>Configure</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {venue.status === 'loaded' && availableGates.length === 0 && (
        <EmptyState
          title="This venue has no gates"
          action={
            <Link
              to={`/o/${organizationId}/venues/${event.venue.venueId}`}
              className="gc-button gc-button--primary"
            >
              Add gates to {event.venue.name}
            </Link>
          }
        >
          Gates are part of the venue, so they only need setting up once.
        </EmptyState>
      )}
    </div>
  );
}
